import './myaccount-flyout'
import { html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { fireAnalyticsEvent } from '@gbds/core/src/ts/utils'
import { setAttribute } from '@gbds/core/src/ts/utils/attribute-utils'
import { CartKeys, MissingInfoKeys, NotificationKeys, RewardsKeys } from './../../models/user-info'
import { AuthUserData, ProfileKeys, RewardsState, UserInfoItems, VehicleKeys } from '../../models/user-info'
import { GlobalNav } from './global-nav'
import { Overlay } from '../overlay'
import { AuthData, AuthKeys, UnauthFooterNode, UnauthHeaderNode, UnauthKeys } from '../../models/my-account'
import { AuthEvents, AuthLiterals } from '../../config/auth-enumerations'
import { parseTokenForEmail, parseTokenForName } from '../../utils/auth/verify-token'
import { MYACCOUNT_LOADED } from '../../events/rendering'
import { sendActionToDatadog, sendErrorToDatadog } from '../../utils/datadog-utils'
import { getProfileDisplayName, getProfileInitials } from '../../utils/profile-name-utils'

/**
 * @noInheritDoc
 */

export class GbpeMyaccount extends LitElement {
	@property()
	flyout: string

	@property({ type: String, reflect: true })
	state: AuthLiterals

	@property({ type: Object, reflect: true })
	unauthNavData: UnauthKeys

	@property({ type: Object, reflect: true })
	authNavData: AuthKeys

	@property({ reflect: true, type: Object })
	unauthFlyoutData: Record<string, unknown>

	private _globalNav: GlobalNav
	private _overlay: Overlay
	private _missingInfo: MissingInfoKeys

	@property({ type: Object })
	private _rewards: RewardsKeys

	@property({ type: Array })
	private _vehicles: VehicleKeys[]

	@property({ type: Object })
	private _profile: ProfileKeys

	@property({ type: Object })
	private _cart: CartKeys

	@property({ type: Boolean, reflect: true })
	private _profileFallback: boolean

	@property({ type: Boolean, reflect: true })
	private _profileLoading: boolean

	@property({ type: Boolean, reflect: true })
	private _rewardsFallback: boolean

	private _rewardsState: RewardsState

	@property({ type: Object, reflect: true })
	private _notifications: NotificationKeys

	private readonly _onGbpeAuthStateUpdated = (): void => {
		this.currentAuthState()
		this.requestUpdate()
	}

	constructor() {
		super()
		this.loadAuthoringContent()
		document.addEventListener('flyout-close', () => this.closeFlyout())
		document.addEventListener('gb-myaccountReset', e => this.refreshUserData(e as CustomEvent))
		window.addEventListener(AuthEvents.authStateUpdated, this._onGbpeAuthStateUpdated)
	}

	disconnectedCallback(): void {
		super.disconnectedCallback()
		window.removeEventListener(AuthEvents.authStateUpdated, this._onGbpeAuthStateUpdated)
	}

	private async loadAuthoringContent() {
		window.gbpe.auth.loaded.then(async () => {
			this.currentAuthState()
			this.loadOverlay()
			await this.loadData()
			this._dispatchApiLoaded(MYACCOUNT_LOADED)
		})
	}

	private currentAuthState() {
		if (window.gbpe.auth.currentAuthState === -1) {
			this.state = AuthLiterals.UNAUTH
		} else if (window.gbpe.auth.currentAuthState === 1) {
			this.state = AuthLiterals.AUTH
		}
	}

	private loadOverlay() {
		this._globalNav = document.querySelector('gb-global-nav')
		const misc = this._globalNav.shadowRoot.querySelector('.gn-misc')
		this._overlay = new Overlay(misc)
	}

	private async loadData() {
		if (this.state === AuthLiterals.AUTH) {
			await this.loadUserData()
			this.swapLinks()
		}
	}

	//Loading user data should be independent of each other
	//We only wait for the last promise to complete so that all data is populated by that time
	private async loadUserData() {
		// Start profile first so _profileLoading is set before authNav renders, but do not block
		// independent user data while the profile API is pending.
		this.getProfileData()
		this.getNotificationData()
		this.getCartData()
		this.getKoreaProfileRefresh()
		this.getKoreaCheckJson()
		await this.getRewardsData()
		if (window.location.href.includes('gmenergy')) {
			if (document.cookie.split('; ').find(row => row.startsWith('gm-energy-destroy='))) {
				document.cookie = 'gm-energy-destroy=; domain=.gm.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;' // Delete the cookie
				window.gbpe.auth.logout()
			}
		}
	}

	private async refreshUserData(e: CustomEvent) {
		const itemName = e.detail.itemName
		switch (itemName) {
			case UserInfoItems.profile:
				this.getProfileData()
				break
			case UserInfoItems.rewards:
				this.getRewardsData()
				break
			case UserInfoItems.vehicles:
				this.getVehicleData()
				break
			case UserInfoItems.notifications:
				this.getNotificationData()
				break
			case UserInfoItems.cart:
				this.getCartData()
			// eslint-disable-next-line no-fallthrough
			case UserInfoItems.koreaProfileRefresh:
				this.getKoreaProfileRefresh()
			// eslint-disable-next-line no-fallthrough
			case UserInfoItems.koreaCheckJson:
				this.getKoreaCheckJson()
		}
	}

	private async getProfileData() {
		const hasExistingValidProfile = this._hasValidProfile()
		this._profileLoading = !hasExistingValidProfile
		this._dispatchProfileUpdate()

		try {
			const profileData = await window.gbpe.user.profile
			this._profile = profileData.data

			// Track profile API response
			sendActionToDatadog('myaccount-nav', 'profile_api_response', {
				status: this._profile?.status,
				has_first_name: !!this._profile?.first_name,
				has_last_name: !!this._profile?.last_name,
				success: this._profile?.status?.toUpperCase() === 'SUCCESS',
			})

			if (
				this._profile?.status?.toUpperCase() === 'SUCCESS' &&
				this._profile.first_name !== undefined &&
				this._profile.last_name !== undefined
			) {
				this._profileFallback = false
				if (this.authNavData.myAccName === 'oneInitial') {
					this._profile.initial = String(this._profile.first_name).charAt(0)
				} else if (this.authNavData.myAccName === 'firstName') {
					this._profile.initial = String(this._profile.first_name)
				} else {
					this._profile.initial = getProfileInitials(this._profile.first_name, this._profile.last_name)
				}
				//Do the regionRedirect here if a user's region does not match with the current url
				this._regionRedirect()
			} else {
				this._profile = null
				this._profileFallback = true
				sendActionToDatadog('myaccount-nav', 'profile_api_fallback', {
					reason: 'invalid_profile_data',
				})
			}
		} catch (e) {
			console.log('Profile API Call Failed!!')
			sendErrorToDatadog('myaccount-nav', e as Error, {
				context: 'profile_api_call',
			})
			this._profileFallback = true
		} finally {
			this._profileLoading = false
			this._dispatchProfileUpdate()
		}
	}

	private _hasValidProfile(): boolean {
		return !!(
			!this._profileFallback &&
			this._profile?.status?.toUpperCase() === 'SUCCESS' &&
			this._profile.first_name !== undefined &&
			this._profile.last_name !== undefined
		)
	}

	private _dispatchProfileUpdate() {
		const detail = {
			profile: this._profile,
			profileFallback: this._profileFallback,
			profileLoading: this._profileLoading,
		}
		document.dispatchEvent(new CustomEvent('gb-updateProfile', { detail, bubbles: true }))
	}

	private swapLinks() {
		window.gbpe.authLinkService.swapLinks()
	}

	//Function to redirect to region mismatch page is needed
	//Called after getting proper Profile information successfully
	private _regionRedirect() {
		try {
			//Get the country first, then check if country matches or not
			const country = this._profile?.['country']
			const redirectRequired = country !== undefined && country !== window.gbpe.config.region
			const language = window.gbpe.config.locale.split('-')[0]

			const countryGBPE = window?.gbpe?.config?.locale?.split('-')[1]
			console.log(`Logged in User's Country: ${country}\nSite's Detected Country: ${countryGBPE}`)
			let brandsDetourURL = ''

			const detourCountries = [
				'US',
				'KR',
				'CA',
				'CH',
				'SE',
				'DE',
				'FR',
				'GB',
				'NL',
				'NO',
				'LO',
				'IT',
				'AU',
				'NZ',
				'IR',
				'NO',
			]

			if (detourCountries.includes(countryGBPE)) {
				if (countryGBPE === 'US') {
					brandsDetourURL = '/detour'
				} else if (countryGBPE === 'CA') {
					brandsDetourURL = '/en/detour'
				} else {
					brandsDetourURL = `/${countryGBPE}-en/detour`
				}
			} else {
				console.log(`Site's Country Not Configured for Detour\nDetected Site's Country: ${countryGBPE}`)
			}

			//Determine if we need to redirect based on the country from Profile API and the current config country
			//And if we're not already on the page to begin with
			if (redirectRequired === true && location.href.includes(brandsDetourURL) !== true) {
				window.location.assign(brandsDetourURL)
			}
		} catch (e) {
			console.log('Unable to parse Profile Data for Region Redirect: ', e)
		}
	}

	private async getRewardsData() {
		try {
			if (this.unauthNavData.enableAuthRewards === 'true') {
				const rewardsData = await window.gbpe.user.rewards
				this._rewards = rewardsData.data['reward']

				// Track rewards API response
				sendActionToDatadog('myaccount-nav', 'rewards_api_response', {
					status: this._rewards?.status,
					has_points: !!this._rewards?.points,
					points: this._rewards?.points,
					resp_code: this._rewards?.RespCode,
					success: !!this._rewards,
				})
				// Redirect if user is on /rewards, /en/rewards, or /fr/rewards and has points
				const path = window.location.pathname
				const rewardsRedirectMap: Record<string, string> = {
					'/rewards': '/myaccount/rewards/dashboard',
					'/en/rewards': '/en/myaccount/rewards/dashboard',
					'/fr/rewards': '/fr/myaccount/rewards/dashboard',
				}
				if (
					this._rewards?.points &&
					Number(String(this._rewards.points).replace(/,/g, '')) >= 0 &&
					rewardsRedirectMap[path]
				) {
					window.location.replace(rewardsRedirectMap[path])
				}
			}
			this._rewards ? this.evalRewardsState() : (this._rewardsFallback = true)

			if (this._rewardsFallback) {
				sendActionToDatadog('myaccount-nav', 'rewards_api_fallback', {
					reason: 'no_rewards_data',
				})
			}

			const detail = {
				rewards: this._rewards,
				rewardsState: this._rewardsState,
			}
			document.dispatchEvent(new CustomEvent('gb-updateRewards', { detail, bubbles: true }))
			console.log('Rewards state: ' + this._rewardsState)
		} catch (e) {
			console.log('Rewards API Call Failed!!')
			sendErrorToDatadog('myaccount-nav', e as Error, {
				context: 'rewards_api_call',
			})
			this._rewardsFallback = true
		}
	}

	private evalRewardsState() {
		switch (this._rewards.status?.toLowerCase()) {
			case 'open':
			case 'active':
			case '200':
				this._rewardsState = RewardsState.active
				break
			case 'suspended':
			case 'inactive':
				this._rewardsState = RewardsState.inactive
				break
			case 'closed':
				this._rewardsState = RewardsState.closed
				break
			default:
				if (this._rewards.RespCode === '601') {
					this._rewardsState = RewardsState.closed
				} else {
					this._rewardsState = RewardsState.other
				}
		}
	}

	private async getVehicleData() {
		try {
			const vehicleData = await window.gbpe.user.vehicles
			this._vehicles = vehicleData.data['garage']

			// Track vehicles API response
			sendActionToDatadog('myaccount-nav', 'vehicles_api_response', {
				vehicle_count: this._vehicles?.length || 0,
				has_vehicles: (this._vehicles?.length || 0) > 0,
				success: !!this._vehicles,
			})
		} catch (e) {
			sendErrorToDatadog('myaccount-nav', e as Error, {
				context: 'vehicles_api_call',
			})
		}
	}

	private async getKoreaProfileRefresh() {
		const cartData = await window.gbpe.user.koreaProfileRefresh
	}

	private async getKoreaCheckJson() {
		const cartData = await window.gbpe.user.koreaCheckJson
	}

	//only Get Cart data when cart is present in the nav.
	private async getCartData() {
		try {
			const shoppingCart = document
				.querySelector('gb-global-nav')
				.shadowRoot.querySelectorAll('.gn-main-link .gn-shop-icon')[0]
				? true
				: false

			if (!shoppingCart) return

			const cartData = await window.gbpe.user.cart
			this._cart = cartData.data.data['cart']

			// Track cart API response
			sendActionToDatadog('myaccount-nav', 'cart_api_response', {
				has_cart: !!this._cart,
				item_count: this._cart?.['order']?.itemCount || 0,
				cart_full: (this._cart?.['order']?.itemCount || 0) > 0,
				success: !!this._cart,
			})

			if (this._cart) {
				if (this._cart['order'].itemCount > 0) {
					document
						.querySelector('gb-global-nav')
						.shadowRoot.querySelectorAll('.gn-main-link .gn-shop-icon')[0]
						.classList.add('gn-cart-full')
				} else {
					document
						.querySelector('gb-global-nav')
						.shadowRoot.querySelectorAll('.gn-main-link .gn-shop-icon')[0]
						.classList.remove('gn-cart-full')
				}
			}
		} catch (e) {
			console.error('Shopping Cart API call failed, ', e)
			sendErrorToDatadog('myaccount-nav', e as Error, {
				context: 'cart_api_call',
			})
		}
	}

	//TODO: Make sure that this is only called when the authNavData is authored with msg inbox link set to true
	//TODO: Make sure that this is only called when the authNavData is authored with msg inbox link set to true
	private async getNotificationData() {
		try {
			const notificationData = await window.gbpe.user.notifications
			this._notifications = {
				notificationOn: notificationData.data?.unreadCount && notificationData.data?.unreadCount > 0,
				unreadCount: notificationData.data.unreadCount,
			}

			// Track notifications API response
			sendActionToDatadog('myaccount-nav', 'notifications_api_response', {
				has_unread: this._notifications.notificationOn,
				unread_count: this._notifications.unreadCount,
				success: true,
			})
		} catch (e) {
			console.error('Notification API call failed, ', e)
			sendErrorToDatadog('myaccount-nav', e as Error, {
				context: 'notifications_api_call',
			})
			this._notifications = {
				notificationOn: false,
				unreadCount: 0,
			}
		}
	}

	private openFlyout() {
		setAttribute(this, 'flyout', 'open')
		this.setGlobalNavProperties()
		const authUserData: AuthUserData = {
			profile: this._profile,
			profileFallback: this._profileFallback,
			profileLoading: this._profileLoading,
			rewards: this._rewards,
			vehicles: this._vehicles,
			missingInfo: this._missingInfo,
			notification: this._notifications,
		}

		const data: AuthData = {
			authenticationState: this.state,
			unauthNavData: this.unauthNavData,
			unauthFlyoutHeaderData: this.unauthFlyoutData['header'] as UnauthHeaderNode,
			unauthFlyoutFooterData: this.unauthFlyoutData['footer'] as UnauthFooterNode,
			myaaccountOverlay: this._overlay,
			userData: authUserData,
			rewardsState: this._rewardsState,
			authNavData: this.authNavData,
		}
		fireAnalyticsEvent('gbpe', 'myAccountFlyout', 'open', 'interaction', data)
	}

	private setGlobalNavProperties() {
		this._overlay.setZHeight()
		this._overlay.closeOverlayOnClick = true
		this._overlay.toggleOverlay()
	}

	private closeFlyout() {
		setAttribute(this, 'flyout', 'close')
	}

	private _getCachedName(): string | null {
		const idTokenName =
			window.gbpe.auth.idToken && window.gbpe.auth.idToken !== ''
				? parseTokenForName(window.gbpe.auth.idToken)
				: null
		const idTokenEmail =
			window.gbpe.auth.idToken && window.gbpe.auth.idToken !== ''
				? parseTokenForEmail(window.gbpe.auth.idToken)
				: null

		return idTokenName || idTokenEmail || 'My Account'
	}

	/***
	 * Unauth state - Event fires after flyouts are loaded
	 * Auth state - Event fires after rewards API is loaded
	 * This is the closest indicator for the nav load
	 * @param name event to fire.
	 */
	private async _dispatchApiLoaded(name: string) {
		await this._globalNav?.flyoutService?.allFlyoutsLoaded
		window.dispatchEvent(new CustomEvent(name))
	}

	private authNav() {
		if (this._hasValidProfile()) {
			return html`<myacc-navigation-header auth data-initial=${this._profile.initial}>
				${this.authNavData.disableRewardsInNav === 'true'
					? ''
					: html`<myacc-nav-name>${getProfileDisplayName(this._profile.first_name)}</myacc-nav-name>`}
				<myacc-nav-rewards>
					<span class="lower-text">
						${this._rewardsState === RewardsState.active &&
						this._rewards.points &&
						this.authNavData.disableRewardsInNav !== 'true'
							? this._rewards.points + ' PTS'
							: ''}
					</span>
				</myacc-nav-rewards>
				${this.authNavData['msgInboxLink'] === 'true' && this._notifications?.['notificationOn']
					? html`<myacc-inbox-notification aria-label="Notification"></myacc-inbox-notification>`
					: ''}
			</myacc-navigation-header>`
		}

		if (this._profileLoading) {
			return html`<myacc-navigation-header unauth fallback>
				<myacc-nav-image><img src=${this.unauthNavData.avatarImg} alt="${this.unauthNavData.avatarImgAltText}"></img></myacc-nav-image>
			</myacc-navigation-header>`
		}

		if (this._profileFallback) {
			const _cachedName = this._getCachedName()
			return html`<myacc-navigation-header unauth fallback>
				<myacc-nav-image><img src=${this.unauthNavData.avatarImg} alt="${
				this.unauthNavData.avatarImgAltText
			}"></img></myacc-nav-image>
				<myacc-nav-fallback>
					<myacc-nav-name>${_cachedName}</myacc-nav-name>
					<myacc-nav-rewards>
						<span class="lower-text">${
							this._rewardsState === RewardsState.active && this._rewards.points
								? this._rewards.points + ' PTS'
								: ''
						}</span>
					</myacc-nav-rewards>
				</myacc-nav-fallback>
			</myacc-navigation-header>`
		}

		return this.unauthNav()
	}

	private unauthNav() {
		return html`<myacc-navigation-header unauth>
			<myacc-nav-image><img src=${this.unauthNavData.avatarImg} alt="${this.unauthNavData.avatarImgAltText}"></img></myacc-nav-image>
			<myacc-nav-text><myacc-button class="gf-main-button">${this.unauthNavData.avatarLabel}</myacc-button></myacc-nav-text>
			</myacc-navigation-header>`
	}

	private navOnClick() {
		switch (this.state) {
			case AuthLiterals.UNAUTH:
				if (this.unauthNavData.disableUnauthFlyout === 'true') {
					window.gbpe.auth.loginRedirect()
				} else {
					this.openFlyout()
				}
				break
			case AuthLiterals.AUTH:
				if (this.authNavData.disableAuthFlyout === 'true') {
					this.redirectToAuthoredLink()
				} else {
					this.openFlyout()
				}
				break
		}
	}

	private redirectToAuthoredLink() {
		window.location.href = this.authNavData.disableFlyoutRedirect
	}

	render() {
		return html`
			<button class="gf-main-button stat-text-link" @click="${this.navOnClick}" data-dtm="global nav">
				${this.state === AuthLiterals.AUTH ? this.authNav() : this.unauthNav()}
			</myacc-navigation-header>
			</button>`
	}

	createRenderRoot() {
		return this
	}
}
